<template>
  <section
    class="fixed inset-0 flex items-center justify-center z-[32] bg-black bg-opacity-30 backdrop-blur-sm mt-[70px]">
    <div id="modal modal-table"
      class="modal absolute block bg-white dark:bg-dark-800 max-w-lg shadow-md rounded-lg dark:shadow-dark-600" :class="{
        'animate__animated animate__zoomIn': showModal,
        'animate__animated animate__zoomOut': !showModal,
      }">
      <div id="modal-header" class="w-full bg-primary dark:bg-dark-600 py-3 rounded-t-lg">
        <h5 class="text-white text-center font-bold text-xl">Pilih Cash Register</h5>
      </div>
      <div id="modal-body" class="p-5 space-y-4 max-h-[calc(100dvh-220px)] overflow-y-auto">
        <div class="flex flex-col gap-2">
          <label class="font-medium text-dark-600 dark:text-dark-400">Range Tanggal</label>
          <DatePicker v-model="rangeDate" selectionMode="range" :manualInput="false"
            @update:modelValue="fetchCashRegisters({ page: 1 })" dateFormat="yy-mm-dd" showIcon class="w-full" />
        </div>
        <TableComponent>
          <TableHead>
            <TableRow>
              <TableHeadCell>No</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
              <TableHeadCell>Cash Register</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow v-if="allCashRegisters.length == 0">
              <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500" noTextColor>
                Belum ada data
              </TableCell>
            </TableRow>
            <TableRow v-else v-for="(cashRegister, index) in allCashRegisters" :key="cashRegister.id" :index="index"
              isBody>
              <TableCell>{{ index + 1 }}</TableCell>
              <TableCell class="text-gray-500 flex">
                <button @click="selectCashRegister(cashRegister)"
                  class="bg-success hover:bg-hover-success flex justify-center items-center mx-1 p-2 rounded-md group">
                  <CheckIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                </button>
              </TableCell>
              <TableCell>{{ formatCashRegisterTime(cashRegister) }}</TableCell>
            </TableRow>
          </TableBody>
        </TableComponent>
        <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
      </div>
      <div class="modal-footer p-5">
        <div class="flex justify-center">
          <SubmitButton id="close" button_name="Close"
            button_class="mx-1 border border-primary bg-white dark:bg-dark-800 dark:border-dark-100 hover:bg-hover-danger hover:border-white hover:text-white text-primary dark:text-white"
            @click="closeModal" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import SubmitButton from '../form/SubmitButton.vue';
  import { mapGetters } from 'vuex';
  import { TableComponent, TableHead, TableBody, TableRow, TableCell, TableHeadCell } from '@/components/table';
  import { CheckIcon } from '@heroicons/vue/24/solid';
  import PaginationButtons from '../PaginationButtons.vue';
  import { formatTime } from '@/helpers/formatTime';
  import { DatePicker } from 'primevue';

  export default {
    name: "ModalChooseCustomer",
    components: {
      SubmitButton, TableComponent, TableHead, TableBody, TableRow, TableCell, TableHeadCell, CheckIcon, PaginationButtons, DatePicker
    },
    data() {
      return {
        showModal: true,
        search: '',
        currentPage: 1,
        totalPages: 1,
        rangeDate: null,
        searchTimeout: null,
      }
    },
    computed: {
      ...mapGetters('cash_registers', ['allCashRegisters']),
    },
    watch: {
      currentPage: {
        immediate: true,
        handler(newVal) {
          this.fetchCashRegisters({ page: newVal });
        },
      },
      search: {
        immediate: true,
        handler(newVal) {
          this.fetchCashRegisters({ search: newVal });
        },
      },
      rangeDate: {
        immediate: true,
        handler(newVal) {
          if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
          }

          this.searchTimeout = setTimeout(() => {
            if (newVal) {
              const start_date = this.formateISODate(newVal[0]);
              const end_date = this.formateISODate(newVal[1]);
              this.fetchCashRegisters({ start_date, end_date, date_column: 'time_start' });
            } else {
              this.fetchCashRegisters();
            }
          }, 1500);
        },
      },
    },
    methods: {
      formatTime,
      formatDate(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
      },
      formateISODate(date) {
        const localDate = new Date(date);
        const offset = localDate.getTimezoneOffset();
        const adjustedDate = new Date(localDate.getTime() - (offset * 60 * 1000));
        return adjustedDate.toISOString().split('T')[0];
      },
      closeModal() {
        this.showModal = false;
        setTimeout(() => {
          this.$emit('closeModal');
        }, 200);
      },
      formatCashRegisterTime(cashRegister) {
        if (!cashRegister) return '';
        return `${formatTime(cashRegister.time_start)} - ${formatTime(cashRegister.time_end)}`;
      },
      async fetchCashRegisters(params = { page: 1, limit: 50 }) {
        if (!params.page) params.page = this.currentPage;
        if (!params.limit) params.limit = 50;

        try {
          await this.$store.dispatch('cash_registers/fetchCashRegisters', params);
        } catch (error) {
          console.error('Error fetching cash registers:', error);
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message,
            life: 3000
          });
        }
      },
      selectCashRegister(cashRegister) {
        this.$confirm.require({
          group: 'headless',
          header: `Apakah anda yakin memilih cash register ${cashRegister.name}?`,
          message: 'Silahkan konfirmasi sebelum diproses.',
          acceptLabel: 'Ya',
          rejectLabel: 'Tidak',
          accept: () => {
            this.$emit('selectCashRegister', cashRegister);
            this.closeModal();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Cash register berhasil dipilih', life: 3000 });
          },
          reject: () => {
            this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Kamu telah membatalkan aksi memilih cash register', life: 3000 });
          }
        });
      }
    },
    created() {
      this.$store.dispatch('cash_registers/fetchCashRegisters');
    }
  }
</script>
